import React, { useEffect, useState } from 'react';
import {
    Table,
    Tag,
    Switch,
    Modal,
    Checkbox,
    message,
    Typography,
    Input,
    Skeleton,
    Row,
    Col,
    Space,
    Popconfirm,
    Segmented,
} from 'antd';
import FormButton from '../components/common/formbutton';
import CancelButton from '../components/common/cancelbutton';
import { getUsers, toggleUser, assignUserRoles } from '../services/userservice';
import { getActiveRoles } from '../services/roleservice';
import { User, Role } from '../types/types';
import { useAuth } from '../auth/authcontext';

type StatusFilter = 'all' | 'active' | 'inactive';

const Users: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [pageLoading, setPageLoading] = useState<boolean>(true);
    const [togglingUser, setTogglingUser] = useState<string | null>(null);
    const [savingRoles, setSavingRoles] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const { isReadOnly } = useAuth();

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, []);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const filtered = users.filter((user) => {
            const matchesQuery =
                `${user.firstName} ${user.lastName}`.toLowerCase().includes(term) ||
                user.email.toLowerCase().includes(term);

            const matchesStatus =
                statusFilter === 'all'
                    ? true
                    : statusFilter === 'active'
                        ? user.active
                        : !user.active;

            return matchesQuery && matchesStatus;
        });
        setFilteredUsers(filtered);
    }, [searchTerm, users, statusFilter]);

    const fetchUsers = async () => {
        setPageLoading(true);
        try {
            const res = await getUsers();
            setUsers(res.data);
            setFilteredUsers(res.data);
        } catch (err) {
            message.error('Failed to load users');
        } finally {
            setPageLoading(false);
        }
    };

    const fetchUsersWhileToggle = async () => {
        try {
            const res = await getUsers();
            setUsers(res.data);
            setFilteredUsers(res.data);
        } catch (err) {
            message.error('Failed to load users');
        }
    };

    const fetchRoles = async () => {
        try {
            const res = await getActiveRoles();
            setRoles(res.data);
        } catch (err) {
            message.error('Failed to load roles');
        }
    };

    const toggleUserStatus = async (userGuid: string) => {
        setTogglingUser(userGuid);
        try {
            await toggleUser(userGuid);
            await fetchUsersWhileToggle();

            const updatedUser = users.find((u) => u.guid === userGuid);
            if (updatedUser) {
                const newStatus = !updatedUser.active;
                message.success(
                    `"${updatedUser.firstName} ${updatedUser.lastName}" is now ${newStatus ? 'active' : 'inactive'
                    }.`
                );
            }
        } catch (err) {
            message.error('Failed to update user status');
        } finally {
            setTogglingUser(null);
        }
    };

    const openRoleModal = (user: User) => {
        setSelectedUser(user);
        
        const readRole = roles.find(r => r.roleName === 'Read');
        const userRoleGuids = user.roles.map((r) => r.guid);
        
        // Force the Read role to be selected by default if they don't have it for some reason
        if (readRole && !userRoleGuids.includes(readRole.guid)) {
            userRoleGuids.push(readRole.guid);
        }

        setSelectedRoles(userRoleGuids);
        setIsModalOpen(true);
    };

    const saveRoles = async () => {
        if (!selectedUser) return;
        setSavingRoles(true);
        try {
            await assignUserRoles(selectedUser.guid, selectedRoles);
            message.success(`Roles updated for ${selectedUser.firstName} ${selectedUser.lastName}`);
            setIsModalOpen(false);
            fetchUsers();
        } catch (err) {
            message.error('Failed to update roles');
        } finally {
            setSavingRoles(false);
        }
    };

    const renderTableSkeleton = () => (
        <Space direction="vertical" style={{ width: '100%' }}>
            {[...Array(6)].map((_, index) => (
                <Row key={index} gutter={16} style={{ marginBottom: 12 }}>
                    <Col span={5}>
                        <Skeleton.Input active size="small" style={{ width: '100%' }} />
                    </Col>
                    <Col span={5}>
                        <Skeleton.Input active size="small" style={{ width: '100%' }} />
                    </Col>
                    <Col span={6}>
                        <Skeleton.Input active size="small" style={{ width: '100%' }} />
                    </Col>
                    <Col span={4}>
                        <Skeleton.Button active size="small" style={{ width: '100%' }} />
                    </Col>
                    <Col span={4}>
                        <Skeleton.Button active size="small" style={{ width: '100%' }} />
                    </Col>
                </Row>
            ))}
        </Space>
    );

    return (
        <>
            <Typography.Title>Users</Typography.Title>

            <Space style={{ marginBottom: 16 }} wrap>
                <Input.Search
                    placeholder="Search by name or email"
                    allowClear
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ maxWidth: 300 }}
                />

                <Segmented
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val as StatusFilter)}
                    options={[
                        { label: 'All', value: 'all' },
                        { label: 'Active', value: 'active' },
                        { label: 'Inactive', value: 'inactive' },
                    ]}
                />
            </Space>

            {pageLoading ? (
                renderTableSkeleton()
            ) : (
                <Table
                    dataSource={filteredUsers}
                    rowKey="guid"
                    pagination={{
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        defaultPageSize: 10,
                    }}
                >
                    <Table.Column
                        title="Name"
                        sorter={(a: User, b: User) =>
                            `${a.firstName} ${a.lastName}`.localeCompare(
                                `${b.firstName} ${b.lastName}`
                            )
                        }
                        render={(user: User) => `${user.firstName} ${user.lastName}`}
                    />
                    <Table.Column title="Email" dataIndex="email" />
                    <Table.Column
                        title="Roles"
                        render={(user: User) => (
                            <>
                                {user.roles.map((role) => (
                                    <Tag color="blue" key={role.guid}>
                                        {role.roleName}
                                    </Tag>
                                ))}
                            </>
                        )}
                    />
                    <Table.Column
                        title="Active"
                        sorter={(a: User, b: User) => Number(a.active) - Number(b.active)}
                        render={(user: User) => (
                            <Popconfirm
                                title={`Are you sure you want to ${user.active ? 'deactivate' : 'activate'
                                    } this user?`}
                                onConfirm={() => toggleUserStatus(user.guid)}
                                okText="Yes"
                                cancelText="No"
                                disabled={isReadOnly}
                            >
                                <Switch
                                    checked={user.active}
                                    size="small"
                                    loading={togglingUser === user.guid}
                                    disabled={isReadOnly}
                                />
                            </Popconfirm>
                        )}
                    />
                    <Table.Column
                        title="Actions"
                        render={(user: User) => (
                            <FormButton
                                title="Manage Roles"
                                onClick={() => openRoleModal(user)}
                                tooltip="Manage Roles"
                            />
                        )}
                    />
                </Table>
            )}

            <Modal
                title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        Manage Roles for {selectedUser?.firstName} {selectedUser?.lastName}
                    </Typography.Title>
                }
                open={isModalOpen}
                footer={null}
                onCancel={() => !savingRoles && setIsModalOpen(false)}
                styles={{ body: { padding: 2 } }}
            >
                <div
                    style={{
                        background: '#fafafa',
                        padding: 16,
                        borderRadius: 8,
                        marginBottom: 24,
                        border: '1px solid #f0f0f0',
                    }}
                >
                    <Typography.Text strong style={{ display: 'block', marginBottom: 12 }}>
                        Select roles to assign:
                    </Typography.Text>

                    {roles.length > 0 ? (
                        <>
                            <Checkbox
                                checked={selectedRoles.length === roles.length}
                                indeterminate={
                                    selectedRoles.length > 0 && selectedRoles.length < roles.length
                                }
                                onChange={(e) => {
                                    const readRole = roles.find(r => r.roleName === 'Read');
                                    if (e.target.checked) {
                                        setSelectedRoles(roles.map((r) => r.guid));
                                    } else {
                                        // When unchecking "Select All", force "Read" to remain checked
                                        setSelectedRoles(readRole ? [readRole.guid] : []);
                                    }
                                }}
                                style={{
                                    marginBottom: 12,
                                    padding: '6px 12px',
                                    backgroundColor: '#f0f5ff',
                                    borderRadius: 6,
                                    fontWeight: 500,
                                    border: '1px solid #d6e4ff',
                                }}
                            >
                                Select All
                            </Checkbox>

                            <Checkbox.Group
                                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                                options={roles.map((role) => ({
                                    label: role.roleName,
                                    value: role.guid,
                                    disabled: role.roleName === 'Read', // Prevent unchecking Read
                                }))}
                                value={selectedRoles}
                                onChange={(checkedValues) => {
                                    let newSelection = checkedValues as string[];
                                    const readRole = roles.find(r => r.roleName === 'Read');
                                    // Ant Design Checkbox Group handles disabled preservation usually, 
                                    // but we strictly enforce it just in case:
                                    if (readRole && !newSelection.includes(readRole.guid)) {
                                        newSelection = [...newSelection, readRole.guid];
                                    }
                                    setSelectedRoles(newSelection);
                                }}
                            />
                        </>
                    ) : (
                        <Typography.Text type="secondary">No roles available</Typography.Text>
                    )}
                </div>

                <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <CancelButton onClick={() => setIsModalOpen(false)} disabled={savingRoles} />
                    <FormButton
                        title="Save"
                        onClick={saveRoles}
                        loading={savingRoles}
                        tooltip="Save changes"
                    />
                </Space>
            </Modal>
        </>
    );
};

export default Users;